import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, MapPin, Play, X, Plus, Check } from "lucide-react";

const ROUND_TYPES = [
  { id: "captura", label: "Solo captura", desc: "Registra tu score, sin competencia" },
  { id: "competitiva", label: "Competitiva", desc: "Match o Medal, con o sin hándicap" },
  { id: "apuesta", label: "Por apuesta", desc: "Formatos con valor por punto" },
];

const BET_FORMATS = [
  { id: "foursome_gcc", label: "Foursome GCC", desc: "2v2 clásico con ventajas y UV", ready: true, min: 4 },
  { id: "units", label: "Unidades", desc: "Puntos por diferencia de golpes", ready: true, min: 2 },
  { id: "match_play", label: "Match Play", desc: "1v1 hoyo por hoyo", ready: true, min: 2 },
  { id: "nassau", label: "Nassau", desc: "Front 9 / Back 9 / Total", ready: true, min: 2 },
  { id: "oyes", label: "Oyeses", desc: "Apuestas en pares 3", ready: true, min: 2 },
  { id: "skins", label: "Skins", desc: "Gana el hoyo el score más bajo solo", ready: true, min: 2 },
  { id: "foursome_trad", label: "Foursome Tradicional", desc: "Golpe alterno, una bola por equipo (2v2)", ready: true, min: 4 },
  { id: "custom", label: "Personalizada", desc: "Arma y guarda tu propio formato", ready: true, min: 2 },
];

const STEP_LABELS = { course: "Campo y hoyos", type: "Tipo de ronda", comp: "Competencia", bet: "Formato", teams: "Equipos (2v2)", custom: "Formato personalizado", players: "Jugadores", classic: "Configuración GCC" };

export default function CreateRound() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [courses, setCourses] = useState([]);
  const [creating, setCreating] = useState(false);

  const [name, setName] = useState("");
  const [courseId, setCourseId] = useState("");
  const [teeId, setTeeId] = useState("");
  const [holes, setHoles] = useState(18);
  const [startingHole, setStartingHole] = useState(1);

  const [roundType, setRoundType] = useState("");
  const [compMode, setCompMode] = useState("match");
  const [compUseHcp, setCompUseHcp] = useState(true);
  const [invert, setInvert] = useState(false);
  const [betFormat, setBetFormat] = useState("");
  const [pointValue, setPointValue] = useState("");
  const [unitsAdvantage, setUnitsAdvantage] = useState(false);

  const [players, setPlayers] = useState([{ name: "", handicap_index: "0", tee_id: "" }]);
  const [teams, setTeams] = useState([{ p1: "", h1: "0", p2: "", h2: "0" }, { p1: "", h1: "0", p2: "", h2: "0" }]);
  const [custom, setCustom] = useState({ base: "medal", skins: false, nassau: false, oyes: false, name: "" });
  const [savedFormats, setSavedFormats] = useState([]);

  useEffect(() => {
    fetch(`${API}/whs/courses`).then((r) => r.json()).then(setCourses).catch(() => {});
    fetch(`${API}/v2/custom-formats`, { credentials: "include" }).then((r) => r.json()).then((d) => Array.isArray(d) && setSavedFormats(d)).catch(() => {});
  }, []);

  const selectedCourse = courses.find((c) => c.course_id === courseId);
  const validPlayers = players.filter((p) => p.name.trim());

  const steps = useMemo(() => {
    const s = ["course", "type"];
    if (roundType === "captura") s.push("players");
    else if (roundType === "competitiva") s.push("comp", "players");
    else if (roundType === "apuesta") {
      s.push("bet");
      if (betFormat === "foursome_gcc") s.push("classic");
      else if (betFormat === "foursome_trad") s.push("teams");
      else if (betFormat === "custom") s.push("custom", "players");
      else if (betFormat) s.push("players");
    }
    return s;
  }, [roundType, betFormat]);

  const currentStep = steps[step];
  const isLast = step === steps.length - 1;

  const addPlayer = () => setPlayers([...players, { name: "", handicap_index: "0", tee_id: teeId }]);
  const removePlayer = (idx) => setPlayers(players.filter((_, i) => i !== idx));
  const updatePlayer = (idx, field, val) => setPlayers((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: val } : p)));

  const minPlayers = () => {
    if (roundType === "captura") return 1;
    if (roundType === "apuesta") return BET_FORMATS.find((f) => f.id === betFormat)?.min || 2;
    return compMode === "match" ? 2 : 1;
  };

  const canProceed = () => {
    if (currentStep === "course") return courseId && teeId;
    if (currentStep === "type") return !!roundType;
    if (currentStep === "bet") return !!betFormat;
    if (currentStep === "teams") return teams.every((t) => t.p1.trim() && t.p2.trim());
    if (currentStep === "players") return validPlayers.length >= minPlayers();
    return true;
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const roundTypeApi = roundType === "captura" ? "casual" : roundType === "competitiva" ? "competitive" : "betting";
      const res = await fetch(`${API}/v2/rounds`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          name, round_type: roundTypeApi, course_id: courseId, tee_id: teeId, starting_hole: startingHole, holes_count: holes,
          use_handicap: roundType === "competitiva" ? compUseHcp : true,
          competitive_format: roundType === "competitiva" ? compMode : null,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Error");
      const round = await res.json();
      const pRes = await fetch(`${API}/v2/rounds/${round.round_id}/players`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ players: validPlayers.map((p) => ({ name: p.name.trim(), handicap_index: p.handicap_index, tee_id: p.tee_id || teeId })) }),
      });
      if (!pRes.ok) throw new Error("Error al agregar jugadores");
      const modules = {};
      const amount = pointValue ? parseFloat(pointValue) || 0 : 0;
      if (roundType === "competitiva" && compMode === "match") modules.match_play = { amount: 0 };
      if (roundType === "apuesta" && betFormat === "custom") {
        if (custom.base === "match") modules.match_play = { amount };
        if (custom.base === "puntos") modules.units = { amount };
        if (custom.skins) modules.skins = { amount };
        if (custom.nassau) modules.nassau = { amount };
        if (custom.oyes) modules.oyes = { amount };
      } else if (roundType === "apuesta" && betFormat) {
        modules[betFormat] = { amount };
      }
      if (Object.keys(modules).length > 0) {
        await fetch(`${API}/v2/rounds/${round.round_id}/modules`, {
          method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ modules }),
        });
      }
      await fetch(`${API}/v2/rounds/${round.round_id}/start`, { method: "PUT", credentials: "include" });
      toast.success("Ronda creada");
      navigate(`/play/${round.round_id}`, { state: { user: true } });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateClassic = async () => {
    setCreating(true);
    try {
      const course = courses.find((c) => c.course_id === courseId) || { name: "Guadalajara Country Club" };
      const res = await fetch(`${API}/rounds`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          name, date: new Date().toISOString().slice(0, 10), course: course.name,
          point_value: pointValue ? parseFloat(pointValue) : null,
          starting_hole: startingHole,
          units_advantage_enabled: unitsAdvantage,
          invert_advantages: startingHole === 10 && invert,
        }),
      });
      if (!res.ok) throw new Error("Error al crear la ronda");
      const round = await res.json();
      toast.success("Foursome GCC creada");
      navigate(`/round/${round.round_id}/players`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateTeams = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API}/v2/rounds`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ name, round_type: "betting", course_id: courseId, tee_id: teeId, starting_hole: startingHole, holes_count: holes, game_variant: "foursome_trad" }),
      });
      if (!res.ok) throw new Error("Error");
      const round = await res.json();
      const teamPlayers = teams.map((t) => ({
        name: `${t.p1.trim()} / ${t.p2.trim()}`,
        handicap_index: String(Math.round(0.5 * ((parseFloat(t.h1) || 0) + (parseFloat(t.h2) || 0)))),
        tee_id: teeId,
      }));
      await fetch(`${API}/v2/rounds/${round.round_id}/players`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ players: teamPlayers }) });
      const amount = pointValue ? parseFloat(pointValue) || 0 : 0;
      await fetch(`${API}/v2/rounds/${round.round_id}/modules`, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ modules: { match_play: { amount } } }) });
      await fetch(`${API}/v2/rounds/${round.round_id}/start`, { method: "PUT", credentials: "include" });
      toast.success("Foursome Tradicional creada");
      navigate(`/play/${round.round_id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const saveCustomFormat = async () => {
    if (!custom.name.trim()) return;
    try {
      const modules = [
        ...(custom.base === "puntos" ? ["units"] : []),
        ...(custom.skins ? ["skins"] : []),
        ...(custom.nassau ? ["nassau"] : []),
        ...(custom.oyes ? ["oyes"] : []),
      ];
      const res = await fetch(`${API}/v2/custom-formats`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ name: custom.name.trim(), base: custom.base, modules, use_handicap: true, point_value: pointValue }),
      });
      const saved = await res.json();
      setSavedFormats([saved, ...savedFormats]);
      toast.success("Formato guardado");
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  const loadFormat = (f) => {
    setCustom({ base: f.base || "medal", skins: (f.modules || []).includes("skins"), nassau: (f.modules || []).includes("nassau"), oyes: (f.modules || []).includes("oyes"), name: f.name });
    if (f.point_value != null) setPointValue(String(f.point_value));
  };

  const submit = () => (currentStep === "classic" ? handleCreateClassic() : betFormat === "foursome_trad" ? handleCreateTeams() : handleCreate());

  return (
    <div className="min-h-screen bg-[#F7F3EA] px-4 py-6" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="create-round-page">
      <div className="max-w-xl mx-auto mb-6">
        <button onClick={() => (step > 0 ? setStep(step - 1) : navigate("/dashboard"))} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#103B31] mb-4" data-testid="back-btn">
          <ArrowLeft size={16} /> {step > 0 ? "Atrás" : "Dashboard"}
        </button>
        <h1 className="text-2xl font-black text-[#0F172A] tracking-tight">Nueva Ronda</h1>
        <div className="flex gap-1.5 mt-3">
          {steps.map((s, i) => (
            <div key={s + i} className={`flex-1 h-1.5 rounded-full transition-colors ${i <= step ? "bg-[#103B31]" : "bg-gray-200"}`} />
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-1.5">{STEP_LABELS[currentStep]} ({step + 1}/{steps.length})</p>
      </div>

      <div className="max-w-xl mx-auto">
        {/* COURSE */}
        {currentStep === "course" && (
          <div className="space-y-4" data-testid="step-course">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Nombre de la ronda (opcional)</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Se usará fecha y hora si lo dejas vacío"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#103B31]/20 bg-white" data-testid="round-name-input" />
            </div>
            <div className="grid gap-3">
              {courses.map((c) => (
                <button key={c.course_id} onClick={() => { setCourseId(c.course_id); setTeeId(""); }}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${courseId === c.course_id ? "bg-green-50 border-[#103B31]" : "bg-white border-gray-200"}`} data-testid={`course-${c.course_id}`}>
                  <MapPin size={18} className={courseId === c.course_id ? "text-[#103B31]" : "text-gray-400"} />
                  <div className="flex-1"><p className="font-semibold text-[#0F172A]">{c.name}</p><p className="text-xs text-gray-500">Par {c.par}</p></div>
                </button>
              ))}
            </div>
            {selectedCourse && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Tee de salida</p>
                <div className="flex gap-2 flex-wrap">
                  {selectedCourse.tees.map((t) => (
                    <button key={t.tee_id} onClick={() => setTeeId(t.tee_id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${teeId === t.tee_id ? "bg-[#103B31] text-white border-[#103B31]" : "bg-white text-gray-700 border-gray-200"}`} data-testid={`tee-${t.tee_id}`}>{t.name}</button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Número de hoyos</p>
              <div className="flex gap-2">
                <button onClick={() => setHoles(18)} className={`px-5 py-2 rounded-lg text-sm font-medium border ${holes === 18 ? "bg-[#103B31] text-white border-[#103B31]" : "bg-white text-gray-700 border-gray-200"}`} data-testid="holes-18">18 hoyos</button>
                <button onClick={() => setHoles(9)} className={`px-5 py-2 rounded-lg text-sm font-medium border ${holes === 9 ? "bg-[#103B31] text-white border-[#103B31]" : "bg-white text-gray-700 border-gray-200"}`} data-testid="holes-9">9 hoyos</button>
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Hoyo de salida</p>
              <div className="flex gap-2">
                {[1, 10].map((h) => (
                  <button key={h} onClick={() => setStartingHole(h)} className={`px-5 py-2 rounded-lg text-sm font-medium border ${startingHole === h ? "bg-[#103B31] text-white border-[#103B31]" : "bg-white text-gray-700 border-gray-200"}`} data-testid={`start-hole-${h}`}>Hoyo {h}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TYPE */}
        {currentStep === "type" && (
          <div className="space-y-3" data-testid="step-type">
            <p className="text-sm text-gray-500 mb-2">¿Qué tipo de ronda quieres jugar?</p>
            {ROUND_TYPES.map((t) => (
              <button key={t.id} onClick={() => setRoundType(t.id)} data-testid={`type-${t.id}`}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${roundType === t.id ? "bg-green-50 border-[#103B31]" : "bg-white border-gray-200 hover:border-gray-300"}`}>
                <div className="flex-1"><p className="font-bold text-[#0F172A]">{t.label}</p><p className="text-xs text-gray-500">{t.desc}</p></div>
                {roundType === t.id && <Check size={20} className="text-[#103B31]" />}
              </button>
            ))}
          </div>
        )}

        {/* COMP */}
        {currentStep === "comp" && (
          <div className="space-y-4" data-testid="step-comp">
            <p className="text-xs font-medium text-gray-500 uppercase">Modalidad</p>
            <div className="flex gap-3">
              {[["match", "Match", "Hoyo por hoyo"], ["medal", "Medal", "Total de golpes"]].map(([id, label, desc]) => (
                <button key={id} onClick={() => setCompMode(id)} data-testid={`comp-${id}`}
                  className={`flex-1 p-4 rounded-xl border-2 text-left ${compMode === id ? "bg-green-50 border-[#103B31]" : "bg-white border-gray-200"}`}>
                  <p className="font-bold text-[#0F172A]">{label}</p><p className="text-xs text-gray-500">{desc}</p>
                </button>
              ))}
            </div>
            <button onClick={() => setCompUseHcp(!compUseHcp)} data-testid="comp-hcp"
              className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left ${compUseHcp ? "bg-green-50 border-[#103B31]" : "bg-white border-gray-200"}`}>
              <div className="flex-1"><p className="font-semibold text-[#0F172A]">Con hándicap</p><p className="text-xs text-gray-500">Compara score neto (bruto − hándicap)</p></div>
              {compUseHcp && <Check size={18} className="text-[#103B31]" />}
            </button>
            {startingHole === 10 && compUseHcp && (
              <button onClick={() => setInvert(!invert)} data-testid="comp-invert"
                className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left ${invert ? "bg-green-50 border-[#103B31]" : "bg-white border-gray-200"}`}>
                <div className="flex-1"><p className="font-semibold text-[#0F172A]">Invertir ventajas</p><p className="text-xs text-gray-500">Se aplican como si salieran del hoyo 1</p></div>
                {invert && <Check size={18} className="text-[#103B31]" />}
              </button>
            )}
          </div>
        )}

        {/* BET */}
        {currentStep === "bet" && (
          <div className="space-y-3" data-testid="step-bet">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Valor por punto (opcional)</label>
              <div className="relative w-40">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                <input type="number" inputMode="decimal" min="0" value={pointValue} onChange={(e) => setPointValue(e.target.value)} placeholder="0"
                  className="w-full pl-7 pr-3 py-3 border border-gray-200 rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-[#103B31]/20 bg-white" data-testid="point-value" />
              </div>
            </div>
            <p className="text-xs font-medium text-gray-500 uppercase">Formato</p>
            {BET_FORMATS.map((f) => (
              <button key={f.id} disabled={!f.ready} onClick={() => setBetFormat(f.id)} data-testid={`format-${f.id}`}
                className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${betFormat === f.id ? "bg-green-50 border-[#103B31]" : "bg-white border-gray-200"} ${!f.ready ? "opacity-50" : ""}`}>
                <div className="flex-1"><p className={`font-bold ${f.ready ? "text-[#0F172A]" : "text-gray-400"}`}>{f.label}</p><p className="text-xs text-gray-500">{f.desc}</p></div>
                {betFormat === f.id && <Check size={20} className="text-[#103B31]" />}
              </button>
            ))}
          </div>
        )}

        {/* PLAYERS */}
        {currentStep === "players" && (
          <div className="space-y-4" data-testid="step-players">
            <p className="text-xs text-gray-500">Mínimo {minPlayers()} jugador(es)</p>
            <div className="space-y-3">
              {players.map((p, idx) => (
                <div key={`player-${idx}`} className="bg-white border border-gray-200 rounded-xl p-3 space-y-2">
                  <div className="flex gap-2 items-center">
                    <input value={p.name} onChange={(e) => updatePlayer(idx, "name", e.target.value)} placeholder={`Jugador ${idx + 1}`}
                      className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#103B31]/30 bg-white" data-testid={`player-name-${idx}`} />
                    <input value={p.handicap_index} onChange={(e) => updatePlayer(idx, "handicap_index", e.target.value)} placeholder="HCP"
                      className="w-20 px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-center bg-white" data-testid={`player-hcp-${idx}`} />
                    {players.length > 1 && <button onClick={() => removePlayer(idx)} className="p-1.5 text-gray-300 hover:text-red-500"><X size={16} /></button>}
                  </div>
                  {selectedCourse && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-semibold text-gray-400 uppercase">Tee</span>
                      {selectedCourse.tees.map((t) => {
                        const active = (p.tee_id || teeId) === t.tee_id;
                        return (
                          <button key={t.tee_id} onClick={() => updatePlayer(idx, "tee_id", t.tee_id)} data-testid={`player-tee-${idx}-${t.tee_id}`}
                            className={`px-3 py-1 rounded-full text-xs font-medium border ${active ? "bg-[#103B31] text-white border-[#103B31]" : "bg-white text-gray-700 border-gray-200"}`}>{t.name}</button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button onClick={addPlayer} className="flex items-center gap-1.5 text-sm font-medium text-[#103B31]" data-testid="add-player-btn">
              <Plus size={14} /> Agregar jugador
            </button>
          </div>
        )}

        {/* TEAMS (Foursome Tradicional) */}
        {currentStep === "teams" && (
          <div className="space-y-4" data-testid="step-teams">
            <p className="text-xs text-gray-500">Golpe alterno: una sola bola por equipo. HCP del equipo = 50% de la suma.</p>
            {teams.map((t, ti) => (
              <div key={ti} className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
                <p className="font-bold text-[#103B31] text-sm">Equipo {ti === 0 ? "A" : "B"}</p>
                {["1", "2"].map((n) => (
                  <div key={n} className="flex gap-2">
                    <input value={t[`p${n}`]} onChange={(e) => setTeams((prev) => prev.map((x, i) => (i === ti ? { ...x, [`p${n}`]: e.target.value } : x)))}
                      placeholder={`Jugador ${n}`} className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white" data-testid={`team-${ti}-name-${n}`} />
                    <input value={t[`h${n}`]} onChange={(e) => setTeams((prev) => prev.map((x, i) => (i === ti ? { ...x, [`h${n}`]: e.target.value } : x)))}
                      placeholder="HCP" className="w-20 px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-center bg-white" data-testid={`team-${ti}-hcp-${n}`} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* CUSTOM (Personalizada) */}
        {currentStep === "custom" && (
          <div className="space-y-4" data-testid="step-custom">
            {savedFormats.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-2">Tus formatos guardados</p>
                <div className="flex gap-2 flex-wrap">
                  {savedFormats.map((f) => (
                    <button key={f.format_id} onClick={() => loadFormat(f)} data-testid={`saved-${f.format_id}`} className="px-3 py-1.5 rounded-full text-sm border bg-white text-gray-700 border-gray-200">{f.name}</button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">Base</p>
              <div className="flex gap-2">
                {[["match", "Match"], ["medal", "Medal"], ["puntos", "Puntos"]].map(([id, label]) => (
                  <button key={id} onClick={() => setCustom({ ...custom, base: id })} data-testid={`custom-base-${id}`}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border ${custom.base === id ? "bg-[#103B31] text-white border-[#103B31]" : "bg-white text-gray-700 border-gray-200"}`}>{label}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">Mecánicas extra</p>
              <div className="space-y-2">
                {[["skins", "Skins"], ["nassau", "Nassau"], ["oyes", "Oyeses"]].map(([id, label]) => (
                  <button key={id} onClick={() => setCustom({ ...custom, [id]: !custom[id] })} data-testid={`custom-${id}`}
                    className={`w-full flex items-center justify-between p-3 rounded-xl border-2 text-left ${custom[id] ? "bg-green-50 border-[#103B31]" : "bg-white border-gray-200"}`}>
                    <span className="font-semibold text-[#0F172A]">{label}</span>{custom[id] && <Check size={18} className="text-[#103B31]" />}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">Guardar formato (opcional)</p>
              <div className="flex gap-2">
                <input value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} placeholder="Nombre del formato"
                  className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white" data-testid="custom-name" />
                <Button onClick={saveCustomFormat} disabled={!custom.name.trim()} className="bg-[#103B31] text-white rounded-lg px-4 disabled:opacity-50" data-testid="custom-save">Guardar</Button>
              </div>
            </div>
          </div>
        )}

        {/* CLASSIC (Foursome GCC config) */}
        {currentStep === "classic" && (
          <div className="space-y-5" data-testid="step-classic">
            <button onClick={() => setUnitsAdvantage(!unitsAdvantage)} data-testid="classic-units-advantage"
              className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left ${unitsAdvantage ? "bg-green-50 border-[#103B31]" : "bg-white border-gray-200"}`}>
              <div className="flex-1"><p className="font-semibold text-[#0F172A]">Ventaja de unidades (UV)</p><p className="text-xs text-gray-500">Neutraliza unidades perdidas según la ventaja de hándicap</p></div>
              {unitsAdvantage && <Check size={18} className="text-[#103B31]" />}
            </button>
            {startingHole === 10 && (
              <button onClick={() => setInvert(!invert)} data-testid="classic-invert-advantages"
                className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left ${invert ? "bg-green-50 border-[#103B31]" : "bg-white border-gray-200"}`}>
                <div className="flex-1"><p className="font-semibold text-[#0F172A]">Invertir ventajas</p><p className="text-xs text-gray-500">La mayoría de las ventajas se aplican en la vuelta del 10 (que se juega primero).</p></div>
                {invert && <Check size={18} className="text-[#103B31]" />}
              </button>
            )}
            <p className="text-xs text-gray-400">Después configurarás los jugadores (2 base + 2 o 3 secundarios), verás el resumen en vivo, podrás doblar en los segundos 9 y aplica el Grand Slam.</p>
          </div>
        )}

        <div className="flex gap-3 mt-8">
          {!isLast ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="flex-1 bg-[#103B31] hover:bg-[#092B25] text-white py-5 rounded-xl font-semibold text-base disabled:opacity-50" data-testid="next-btn">
              Siguiente <ArrowRight size={16} className="ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={creating || !canProceed()} className="flex-1 bg-[#103B31] hover:bg-[#092B25] text-white py-5 rounded-xl font-semibold text-base disabled:opacity-50" data-testid={currentStep === "classic" ? "create-classic-btn" : "create-btn"}>
              <Play size={16} className="mr-1" /> {creating ? "Creando..." : currentStep === "classic" ? "Continuar a Jugadores" : "Iniciar Ronda"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
